import React, { useEffect, useState, memo } from 'react';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import InformationList from '../InformationList/InformationList';
import InformationListItem from '../InformationList/InformationListItem';
import { formatMoneyNumeral } from '../../util/helpers/numeralHelpers';
import themeColors from '../../assets/styles/themeColors';
import { pxToRem } from '../../assets/styles/helper';

const PriceListContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 100%;
  border-bottom: 1px solid ${themeColors.colorBorderPrimary}66;
  border-top: 1px solid ${themeColors.colorBorderPrimary}66;
  padding-bottom: 6px;
`;

const ScrollableList = styled.div`
  // flex: 1;
  min-height: 0;
  overflow-y: auto;
  // padding-right: ${pxToRem(4)};
`;

const StickyTotal = styled.div`
  flex-shrink: 0;
  background: ${themeColors.colorBackgroundPrimary};
`;

const OrderReviewPriceList = ({
  paymentMethodInfo,
  paymentDiscount,
  selectedPaymentMethod,
  calculation,
  onClick,
  paymentDiscountValue2,
  isPostPaidVisible,
  totalPrice,
}) => {
  const { t } = useTranslation();
  const [orderInformationListData, setOrderInformationListData] = useState([]);

  // useEffect(() => {
  //   setOrderInformationListData(dataForOrder);
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [paymentMethodInfo, paymentDiscount]);

  // useEffect(() => {
  //   setOrderDiscountedTotal(dataForDiscountedTotal);
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [paymentMethodInfo, paymentDiscount]);

  const estimatedTotal = totalPrice();

  useEffect(() => {
    setOrderInformationListData({
      pricePerUnit: isPostPaidVisible
        ? paymentMethodInfo?.ClientPricePerUnit
        : calculation?.newPricePerUnit,
      estimatedTotal,
      volumeDiscount: isPostPaidVisible
        ? paymentMethodInfo?.VolumeDiscount
        : calculation?.volumeDiscount,
      paymentTypeDiscount: paymentDiscount ?? paymentDiscountValue2,
      salesTax: calculation?.salesTax,
      deliveryFee: calculation?.deliveryFee,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPaymentMethod, paymentMethodInfo, calculation]);

  const dataForOrder = [
    {
      label: t('buyWizard.pricePerUnit'),
      value: formatMoneyNumeral(orderInformationListData?.pricePerUnit),
    },
    isPostPaidVisible && selectedPaymentMethod?.PaymentMethodType !== 3
      ? {
          label: t('buyWizard.cashDiscount'),
          value: `-${formatMoneyNumeral(
            orderInformationListData?.paymentTypeDiscount,
          )}`,
          isValueBold: true,
          isColored: themeColors.colorSuccess,
        }
      : {
          lable: '',
          value: '',
        },
    {
      label: t('buyWizard.volumeDiscount'),
      value: formatMoneyNumeral(orderInformationListData?.volumeDiscount),
      infoIcon: true,
      onClick,
    },
    orderInformationListData?.deliveryFee !== null
      ? {
          label: t('buyWizard.deliveryCost'),
          value: formatMoneyNumeral(orderInformationListData?.deliveryFee),
        }
      : {},
    orderInformationListData?.salesTax !== null
      ? {
          label: t('buyWizard.salesTax'),
          value: formatMoneyNumeral(orderInformationListData?.salesTax),
        }
      : {},
  ];

  const dataForDiscountedTotal = [
    {
      label:
        (isPostPaidVisible &&
          selectedPaymentMethod?.PaymentMethodType !== 3 &&
          orderInformationListData?.paymentTypeDiscount &&
          orderInformationListData?.paymentTypeDiscount > 0) ||
        orderInformationListData?.volumeDiscount > 0
          ? t('buyWizard.discountedTotal')
          : t('buyWizard.total'),
      value: formatMoneyNumeral(orderInformationListData?.estimatedTotal),
      isValueBold: true,
    },
  ];

  return (
    <PriceListContainer>
      <ScrollableList>
        <InformationList marginTop={8}>
          {dataForOrder.map(
            ({
              label,
              value,
              isColored,
              isBold,
              isValueBold,
              infoIcon,
              onClick,
            }) => (
              <InformationListItem
                key={uuidv4()}
                label={label}
                value={value}
                isColored={isColored}
                isBold={isBold}
                isValueBold={isValueBold}
                fullWidth
                infoIcon={infoIcon}
                onClick={onClick}
              />
            ),
          )}
        </InformationList>
      </ScrollableList>

      <StickyTotal>
        <InformationList marginTop={10}>
          {dataForDiscountedTotal.map(
            ({ label, value, isColored, isBold, isValueBold }) => (
              <InformationListItem
                key={uuidv4()}
                label={label}
                value={value}
                isColored={isColored}
                isBold={isBold}
                isValueBold={isValueBold}
                fullWidth
              />
            ),
          )}
        </InformationList>
      </StickyTotal>
    </PriceListContainer>
  );
};

OrderReviewPriceList.propTypes = {
  paymentMethodInfo: PropTypes.shape({
    ClientPricePerUnit: PropTypes.number,
    TotalClientExecutionPrice: PropTypes.number,
    VolumeDiscount: PropTypes.number,
  }),
  paymentDiscount: PropTypes.number,
  selectedPaymentMethod: PropTypes.shape({
    PaymentMethodType: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
  }),
  calculation: PropTypes.shape({
    salesTax: PropTypes.number,
    deliveryFee: PropTypes.number,
    volumeDiscount: PropTypes.number,
    paymentTypeDiscount: PropTypes.number,
    newPricePerUnit: PropTypes.number,
    estimatedOrderTotal: PropTypes.number,
  }),
  onClick: PropTypes.func,
  paymentDiscountValue2: PropTypes.number,
  isPostPaidVisible: PropTypes.bool,
  totalPrice: PropTypes.func,
};

export default memo(OrderReviewPriceList);
